import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import BorrowingTab from './components/BorrowingTab';
import ReturnTab from './components/ReturnTab';
import ReservationTab from './components/ReservationRequestsTab';

const TABS = {
  Borrowing: 'الإعارة',
  Return: 'الإرجاع',
  Reservation: 'طلبات الحجز',
};

const sidebarVariants = {
  hidden: { x: 260 },
  visible: { x: 0, transition: { type: 'tween', duration: 0.4 } },
};

const asideVariants = {
  hidden: { x: '100%' },
  visible: { x: 0, transition: { type: 'tween', duration: 0.3 } },
};

export default function CashierDashboard() {
  const [activeTab, setActiveTab] = useState('Borrowing');
  const [reservationCount, setReservationCount] = useState(0);
  const [viewedReservationIds, setViewedReservationIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('viewedReservationIds')) || [];
    } catch {
      return [];
    }
  });
  const [showSidebar, setShowSidebar] = useState(false);

  const ActiveComponent =
    activeTab === 'Borrowing'
      ? BorrowingTab
      : activeTab === 'Return'
      ? ReturnTab
      : ReservationTab;

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await fetch('/library-api/api/reservations');
        const all = await res.json();
        const pending = all.filter(
          r => r.status === 'pending' && !viewedReservationIds.includes(r._id)
        );
        setReservationCount(pending.length);
      } catch (err) {
        console.error('فشل في جلب طلبات الحجز:', err);
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 5000);
    return () => clearInterval(interval);
  }, [viewedReservationIds]);

  useEffect(() => {
    const markAllViewed = async () => {
      if (activeTab === 'Reservation') {
        try {
          const res = await fetch('/library-api/api/reservations');
          const all = await res.json();
          const newPending = all
            .filter(r => r.status === 'pending')
            .map(r => r._id);
          const updated = Array.from(new Set([...viewedReservationIds, ...newPending]));
          setViewedReservationIds(updated);
          localStorage.setItem('viewedReservationIds', JSON.stringify(updated));
          setReservationCount(0);
        } catch (err) {
          console.error('فشل في تحديث حالة الإشعار:', err);
        }
      }
    };
    markAllViewed();
  }, [activeTab]);

  return (
    <div dir="rtl" className="min-h-screen flex flex-col md:flex-row bg-gray-100 font-sans overflow-hidden">
      {/* زر إظهار القائمة الجانبية في الشاشات الصغيرة */}
      {!showSidebar && (
        <button
          onClick={() => setShowSidebar(true)}
          className="md:hidden fixed top-4 right-4 z-20 bg-blue-600 text-white p-2 rounded-full shadow-lg"
        >
          ☰
        </button>
      )}

      <AnimatePresence>
        {showSidebar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setShowSidebar(false)}
          >
            <motion.aside
              variants={asideVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="fixed inset-y-0 right-0 w-64 bg-white px-4 pt-3 pb-6 flex flex-col justify-start shadow-lg overflow-y-auto z-50 md:hidden"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSidebar(false)}
                className="self-start text-gray-600 hover:text-gray-800 p-2 mb-3"
              >
                ✖
              </button>

              <nav className="flex flex-col gap-3">
                {Object.keys(TABS).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => { setActiveTab(tab); setShowSidebar(false); }}
                    className={`px-4 py-2 text-right rounded-lg font-medium transition relative ${
                      activeTab === tab
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'hover:bg-blue-100 text-gray-700'
                    }`}
                  >
                    {TABS[tab]}
                    {tab === 'Reservation' && reservationCount > 0 && (
                      <span className="absolute -top-2 -left-2 bg-red-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                        {reservationCount}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      {/* الشريط الجانبي في الشاشات الكبيرة */}
      <motion.aside
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        className="hidden md:flex w-64 bg-white border-l p-4 flex-shrink-0 flex-col justify-start shadow-lg"
      >
        <nav className="flex flex-col gap-3">
          {Object.keys(TABS).map((tab) => (
            <motion.button
              key={tab}
              onClick={() => setActiveTab(tab)}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className={`px-4 py-2 text-right rounded-lg font-medium transition relative ${
                activeTab === tab
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'hover:bg-blue-100 text-gray-700'
              }`}
            >
              {TABS[tab]}
              {tab === 'Reservation' && reservationCount > 0 && (
                <span className="absolute -top-2 -left-2 bg-red-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {reservationCount}
                </span>
              )}
            </motion.button>
          ))}
        </nav>
      </motion.aside>

      {/* المحتوى الرئيسي */}
      <main className="flex-1 px-4 md:px-6 py-6 md:py-8 overflow-y-auto">
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-md space-y-6 w-full">
          <ActiveComponent />
        </div>
      </main>
    </div>
  );
}
